import { useState, useCallback, useEffect } from 'react';
import { formatInTimeZone } from 'date-fns-tz';

function useTableDateRange({
  hasDateRange,
  updateQueryParams,
  getQueryParams
}) {
  const [dateRange, setDateRange] = useState();
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper function to get week range
  const getWeekRange = useCallback(() => {
    const tz = 'Pacific/Auckland';
    const today = new Date();
    const todayNZ = new Date(formatInTimeZone(today, tz, 'yyyy-MM-dd'));
    const sevenDaysAgoNZ = new Date(todayNZ);
    sevenDaysAgoNZ.setDate(todayNZ.getDate() - 6);
    return [sevenDaysAgoNZ, todayNZ];
  }, []);

  // Initialize date range from URL or set default
  const initializeDateRange = useCallback(() => {
    if (!hasDateRange) {
      setIsInitialized(true);
      return { initialStartDate: null, initialEndDate: null };
    }

    const params = getQueryParams();
    let initialStartDate, initialEndDate;

    if (params.startDate && params.endDate) {
      initialStartDate = new Date(params.startDate);
      initialEndDate = new Date(params.endDate);
      setDateRange([initialStartDate, initialEndDate]);
    } else {
      // Set default date range if none provided
      const [defaultStart, defaultEnd] = getWeekRange();
      const timeZone = "Pacific/Auckland";
      const from = formatInTimeZone(defaultStart, timeZone, 'yyyy-MM-dd');
      const to = formatInTimeZone(defaultEnd, timeZone, 'yyyy-MM-dd');

      setDateRange([defaultStart, defaultEnd]);
      initialStartDate = defaultStart;
      initialEndDate = defaultEnd;

      // Update URL with default date range
      updateQueryParams({
        startDate: from,
        endDate: to
      }, { replace: true });
    }

    setIsInitialized(true);
    return { initialStartDate, initialEndDate };
  }, [hasDateRange, getQueryParams, getWeekRange, updateQueryParams]);

  // Sync date range with URL changes after initial load
  useEffect(() => {
    if (!hasDateRange) return;

    const params = getQueryParams();
    if (params.startDate && params.endDate) {
      const startDate = new Date(params.startDate);
      const endDate = new Date(params.endDate);
      if (dateRange && (startDate.toISOString() !== dateRange[0]?.toISOString() ||
        endDate.toISOString() !== dateRange[1]?.toISOString())) {
        setDateRange([startDate, endDate]);
      }
    }
  }, [getQueryParams, hasDateRange, dateRange]);

  const handleDateRangeChange = useCallback(async (dates) => {
    if (!hasDateRange) return;

    let from, to;
    if (dates && dates[0] && dates[1]) {
      // Update the dateRange state first
      setDateRange([new Date(dates[0]), new Date(dates[1])]);

      // Format dates for URL
      const timeZone = "Pacific/Auckland";
      from = formatInTimeZone(new Date(dates[0]), timeZone, 'yyyy-MM-dd');
      to = formatInTimeZone(new Date(dates[1]), timeZone, 'yyyy-MM-dd');

      // Update URL params
      updateQueryParams({
        startDate: from,
        endDate: to,
      }, { replace: true });

      // Return the formatted dates for immediate use
      return { from, to };
    }

    return null;
  }, [hasDateRange, updateQueryParams]);

  const getFormattedDateRange = useCallback(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return { from: null, to: null };
    }

    const timeZone = "Pacific/Auckland";
    const from = formatInTimeZone(dateRange[0], timeZone, 'yyyy-MM-dd');
    const to = formatInTimeZone(dateRange[1], timeZone, 'yyyy-MM-dd');

    return { from, to };
  }, [dateRange]);
  
  return {
    dateRange,
    setDateRange,
    handleDateRangeChange,
    initializeDateRange,
    getFormattedDateRange,
    getWeekRange,
    isInitialized
  };
}

export default useTableDateRange;
